import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Axios } from '@utils/api/axios-instance.ts';
import { GET_URL } from '@utils/api/endpoints.ts';
import { DocumentResult } from '@utils/types/editors/generic.ts';
import {
  getBaseline,
  probeServerLastModified,
  setBaseline,
  subscribeRemoteEdit,
  syncKey
} from '@utils/editors/document-sync-baseline.ts';

const POLL_INTERVAL_MS = 15_000;
/** Server timestamp granularity tolerance (both sides come from the server). */
const GRACE_MS = 1000;

interface UseConcurrentEditPollingOptions {
  sessionId: string;
  documentId: string;
  /** Initial last_modified from the document loaded into the editor */
  initialLastModified: Date | string;
  /** Whether to pause polling (e.g. during our own save) */
  isSaving: boolean;
  /** Whether polling is enabled at all */
  enabled?: boolean;
}

interface UseConcurrentEditPollingResult {
  /** True when a remote edit has been detected */
  hasRemoteEdit: boolean;
  /** Dismiss the notification and stop polling permanently */
  dismiss: () => void;
  /** Re-fetch the document and reset polling baseline */
  reload: () => Promise<DocumentResult | null>;
}

/**
 * Watch the server for edits made outside this tab.
 *
 * Same-account concurrency (issue #2347): this used to compare
 * `last_modified_by` against the current user and silently adopt the new server
 * timestamp whenever it matched — on the assumption that our own user id meant
 * our own save. A second tab signed into the same account is indistinguishable
 * under that test, so its edits were adopted as "ours" and the banner never
 * appeared. Worse, adopting the timestamp told this tab it was up to date, which
 * cleared the way for its next autosave to overwrite the other tab's work.
 *
 * The comparison is now purely positional: the baseline in
 * `document-sync-baseline` records the server version *this tab* is based on and
 * advances only when this tab saves or reloads. Anything newer than that came
 * from somewhere else — another user, or this account in another tab — and both
 * cases need the same banner.
 */
export function useConcurrentEditPolling({
  sessionId,
  documentId,
  initialLastModified,
  isSaving,
  enabled = true
}: UseConcurrentEditPollingOptions): UseConcurrentEditPollingResult {
  const [hasRemoteEdit, setHasRemoteEdit] = useState(false);

  const key = useMemo(
    () => syncKey(sessionId, documentId),
    [sessionId, documentId]
  );

  const dismissedRef = useRef(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isSavingRef = useRef(isSaving);

  // Seed the shared baseline from the document that was loaded into the editor.
  // The first render may pass `new Date()` as a placeholder while the document
  // is still loading, so only the first usable value is adopted (setBaseline is
  // monotonic, so a later real value cannot be walked backwards).
  const initialLastModifiedMs = new Date(initialLastModified).getTime();
  const hasSetRealBaseline = useRef(false);
  useEffect(() => {
    if (!hasSetRealBaseline.current && initialLastModifiedMs > 0) {
      setBaseline(key, initialLastModifiedMs);
      hasSetRealBaseline.current = true;
    }
  }, [key, initialLastModifiedMs]);

  // Keep isSaving ref in sync
  useEffect(() => {
    isSavingRef.current = isSaving;
  }, [isSaving]);

  // Grace period: skip the next poll after our own save completes. The saving
  // provider advances the baseline from the PUT response (or a follow-up
  // probe), and this avoids racing that update and reporting our own write as
  // somebody else's.
  const skipNextPollRef = useRef(false);
  const prevIsSavingRef = useRef(isSaving);
  useEffect(() => {
    if (prevIsSavingRef.current && !isSaving) {
      skipNextPollRef.current = true;
    }
    prevIsSavingRef.current = isSaving;
  }, [isSaving]);

  const pollRef = useRef<() => Promise<void>>();

  const flagRemoteEdit = useCallback(() => {
    setHasRemoteEdit(true);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const poll = useCallback(async () => {
    if (dismissedRef.current) return;
    if (isSavingRef.current) return;
    if (document.visibilityState === 'hidden') return;

    if (skipNextPollRef.current) {
      skipNextPollRef.current = false;
      return;
    }

    const serverLastModified = await probeServerLastModified(
      sessionId,
      documentId
    );
    if (serverLastModified == null) return;

    const baseline = getBaseline(key);
    if (baseline <= 0) {
      // No baseline yet (document metadata had no usable last_modified).
      // Adopt the first server reading so later changes can be measured.
      setBaseline(key, serverLastModified);
      return;
    }

    // Deliberately NOT advancing the baseline here: the whole point is that the
    // server moving past our baseline is the conflict, not a new normal.
    if (serverLastModified > baseline + GRACE_MS) {
      flagRemoteEdit();
    }
  }, [sessionId, documentId, key, flagRemoteEdit]);

  // Keep poll ref current for visibilitychange handler
  pollRef.current = poll;

  // Start polling interval
  useEffect(() => {
    if (!enabled || dismissedRef.current || hasRemoteEdit) return;

    // Immediate first check
    poll();

    intervalRef.current = setInterval(poll, POLL_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enabled, poll, hasRemoteEdit]);

  // An autosave that resolved to `conflict` raises the same banner. Polling is
  // skipped in hidden tabs, so autosave is often the first to notice.
  useEffect(() => {
    if (!enabled) return;
    return subscribeRemoteEdit(key, () => {
      if (dismissedRef.current) return;
      flagRemoteEdit();
    });
  }, [enabled, key, flagRemoteEdit]);

  // Immediate check when tab becomes visible
  useEffect(() => {
    if (!enabled) return;

    const handleVisibilityChange = () => {
      if (
        document.visibilityState === 'visible' &&
        !dismissedRef.current &&
        !hasRemoteEdit
      ) {
        pollRef.current?.();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, hasRemoteEdit]);

  const dismiss = useCallback(() => {
    dismissedRef.current = true;
    setHasRemoteEdit(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const reload = useCallback(async (): Promise<DocumentResult | null> => {
    try {
      const url = GET_URL(sessionId, documentId).document;
      const { data } = await Axios.get<DocumentResult>(url, {
        params: { direct_download: true }
      });

      // If backend returns a download_url, fetch from blob storage
      if (data.result?.download_url) {
        const blobResponse = await fetch(data.result.download_url);
        if (blobResponse.ok) {
          const documentContent = await blobResponse.json();
          data.result.document = documentContent;
          data.result.download_url = null;
          data.result.expires_in = null;
        }
      }

      // We are now based on this server version, so the baseline may advance.
      setBaseline(key, data.result.last_modified);
      dismissedRef.current = false;
      setHasRemoteEdit(false);

      // Restart polling
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      intervalRef.current = setInterval(poll, POLL_INTERVAL_MS);

      return data;
    } catch {
      return null;
    }
  }, [sessionId, documentId, key, poll]);

  return { hasRemoteEdit, dismiss, reload };
}
